import { useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Check, Copy, Rocket, Users } from "lucide-react";
import { CA, COIN_IMG, PUMP_URL, copyText, scrollToId } from "@/data";
import { TateLetters } from "@/components/GiantTate";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay, className }) {
  return (
    <span className="block overflow-hidden pb-[0.08em]">
      <motion.span
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.05, ease: EASE, delay }}
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

function FloatBadge({ className, delay, children, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.6, ease: EASE }}
      className={`absolute z-20 ${className}`}
    >
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay }}
        className="glass rounded-full border border-[rgba(229,169,60,0.4)] px-3.5 py-1.5 font-mono2 text-[11px] font-medium tracking-wide text-[#F6C85F] shadow-[0_8px_30px_rgba(0,0,0,0.5)]"
        data-testid={testId}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

export default function HeroKinetic() {
  const [copied, setCopied] = useState(false);
  const frameRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 16 });

  const { scrollY } = useScroll();
  const watermarkY = useTransform(scrollY, [0, 700], [0, 150]);
  const watermarkX = useTransform(scrollY, [0, 700], [0, -60]);

  const onMove = (e) => {
    const r = frameRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const handleCopy = async () => {
    await copyText(CA, "Contract address copied");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section
      id="top"
      className="relative overflow-hidden pt-[72px]"
      data-testid="hero-section"
    >
      {/* backdrop glows */}
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-[#DC2626] opacity-[0.07] blur-[140px]" />
      <div className="pointer-events-none absolute -right-32 top-24 h-[560px] w-[560px] rounded-full bg-[#E5A93C] opacity-[0.09] blur-[160px]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.22]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(229,169,60,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(229,169,60,0.05) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />

      {/* parallax $TATE watermark */}
      <motion.div
        style={{ y: watermarkY, x: watermarkX }}
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 top-16 select-none font-display text-[clamp(10rem,24vw,22rem)] font-black uppercase leading-none text-stroke-faint opacity-70"
      >
        $TATE
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-24 pt-14 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-8 lg:pt-20 [&>*]:min-w-0">
        {/* Left — kinetic type */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.35 }}
            className="mb-7 flex flex-wrap items-center gap-2.5"
          >
            <span className="rounded-full border border-[rgba(229,169,60,0.4)] bg-[#0E1015] px-3.5 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#E5A93C] transition-colors duration-300 hover:bg-[rgba(229,169,60,0.12)]" data-testid="hero-badge-cate">
              🐈 $CATE Lineage
            </span>
            <span className="rounded-full border border-[rgba(220,38,38,0.5)] bg-[#0E1015] px-3.5 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#F87171] transition-colors duration-300 hover:bg-[rgba(220,38,38,0.12)]" data-testid="hero-badge-trump">
              🇺🇸 Trump Energy
            </span>
          </motion.div>

          <h1 className="font-display text-[clamp(3.2rem,9vw,7.5rem)] font-black uppercase leading-[0.92] tracking-tight">
            <MaskedLine delay={0.45}>Make</MaskedLine>
            <MaskedLine delay={0.58} className="text-stroke-gold">Memes</MaskedLine>
            <MaskedLine delay={0.71}>
              Great <span className="text-[#E5A93C]">Again.</span>
            </MaskedLine>
          </h1>

          {/* Custom wordmark reveal */}
          <div className="mt-5 overflow-hidden pb-1 pt-5 sm:pt-6">
            <motion.div
              initial={{ y: "115%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.05, ease: EASE, delay: 0.85 }}
              data-testid="hero-wordmark"
            >
              <TateLetters className="text-[clamp(2.6rem,6.5vw,5.25rem)]" />
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.05 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#94A3B8] sm:text-lg"
          >
            The unapologetic fusion of Trump&apos;s larger-than-life energy and the viral
            chaos of $CATE. Louder memes. Stronger community. One ticker built to be
            impossible to ignore.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.2 }}
            className="mt-9 flex flex-wrap items-center gap-3.5"
          >
            <a
              href={PUMP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-buy-button"
              className="group flex items-center gap-2.5 rounded-full bg-[#E5A93C] px-7 py-3.5 font-display text-base font-bold text-[#07080A] transition-all duration-300 hover:bg-[#F6C85F] hover:shadow-[0_0_36px_rgba(246,200,95,0.4)]"
            >
              <Rocket className="h-4.5 w-4.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              Buy $TATE
            </a>
            <button
              onClick={() => scrollToId("#narrative")}
              data-testid="hero-movement-button"
              className="flex items-center gap-2.5 rounded-full border border-[rgba(229,169,60,0.4)] px-7 py-3.5 font-display text-base font-bold text-[#F8FAFC] transition-all duration-300 hover:border-[#F6C85F] hover:bg-[#0E1015]"
            >
              <Users className="h-4.5 w-4.5 text-[#E5A93C]" />
              Join the Movement
            </button>
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            onClick={handleCopy}
            data-testid="hero-copy-ca-button"
            className="group mt-7 flex max-w-full items-center gap-3 rounded-xl border border-dashed border-[rgba(229,169,60,0.4)] bg-[#0E1015]/70 px-4 py-3 transition-colors duration-300 hover:border-[#F6C85F]"
          >
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
              CA
            </span>
            <span className="truncate font-mono2 text-xs text-[#94A3B8] transition-colors group-hover:text-[#F6C85F] sm:text-sm">
              {CA}
            </span>
            {copied ? (
              <Check className="h-4 w-4 shrink-0 text-[#F6C85F]" />
            ) : (
              <Copy className="h-4 w-4 shrink-0 text-[#64748B] group-hover:text-[#F6C85F]" />
            )}
          </motion.button>
        </div>

        {/* Right — 3D coin spotlight */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.7 }}
          className="relative mx-auto w-full max-w-[440px]"
          style={{ perspective: 1100 }}
        >
          <div
            ref={frameRef}
            onMouseMove={onMove}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            className="relative aspect-square"
            data-testid="hero-coin-spotlight"
          >
            <div className="coin-shine absolute -inset-10 rounded-full" />
            <div className="absolute inset-0 rounded-full bg-[#E5A93C] opacity-20 blur-[90px]" />
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="h-full w-full"
            >
              <motion.div
                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                className="relative h-full w-full"
              >
                <div className="absolute inset-0 rounded-full border border-[rgba(246,200,95,0.5)] shadow-[0_0_80px_rgba(229,169,60,0.25),inset_0_0_60px_rgba(0,0,0,0.6)]" />
                <img
                  src={COIN_IMG}
                  alt="$TATE coin — golden cat with iconic hair"
                  className="h-full w-full rounded-full object-cover"
                  draggable="false"
                />
                <div
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at 32% 26%, rgba(255,235,180,0.22), transparent 42%), radial-gradient(circle at 70% 80%, rgba(0,0,0,0.35), transparent 55%)",
                  }}
                />
              </motion.div>
            </motion.div>
            <FloatBadge className="-left-3 top-[12%] sm:-left-8" delay={1.5} testId="hero-badge-pump">
              ▲ +142.8% TODAY
            </FloatBadge>
            <FloatBadge className="-right-2 top-[42%] sm:-right-7" delay={1.7} testId="hero-badge-whale">
              🐋 WHALE ALERT
            </FloatBadge>
            <FloatBadge className="bottom-[8%] left-[8%]" delay={1.9} testId="hero-badge-lives">
              🐾 9 LIVES LOADED
            </FloatBadge>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
